import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import {
  HiOutlineClipboardDocumentList,
  HiOutlineUserGroup,
  HiOutlineCalculator,
} from "react-icons/hi2";
import Button from "components/button/Button";
import ConfirmModal from "components/modal/ConfirmModal";
import { useModal } from "hooks/useModal";
import {
  QuantitativeValuesPayload,
  QuantitativeValueType,
} from "types/assessmentTypes";
import {
  useGetAssessmentByIdQuery,
  useCompleteAssessmentMutation,
  useAnswerQuantitativeMutation,
  useAdminRestartAssessmentMutation,
  useUserRestartAssessmentMutation,
} from "reduxFiles/api/assessmentApi";

import AssessmentHeader from "./AssessmentHeader";
import AssessmentStats from "./AssessmentStats";
import RespondersSection from "./RespondersSection";
import { useScope } from "hooks/useScope";
import QuantitativeForm from "../quantitative/QuantitativeForm";

export default function AssessmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const scope = useScope();
  const { t } = useTranslation();
  const confirmModal = useModal();
  const updatedConfirmModal = useModal();

  const [activeTab, setActiveTab] = useState<"responders" | "quantitative">(
    "responders"
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isQuantitativeSubmitting, setIsQuantitativeSubmitting] =
    useState(false);
  const [localQuantitativeValues, setLocalQuantitativeValues] = useState<
    QuantitativeValueType[] | undefined
  >(undefined);

  const tabs = [
    {
      id: "responders" as const,
      icon: HiOutlineUserGroup,
      label: t("assessment.detail.tabs.responders"),
    },
    {
      id: "quantitative" as const,
      icon: HiOutlineCalculator,
      label: t("assessment.detail.tabs.quantitative"),
    },
  ];

  const {
    data: assessment,
    isFetching,
    isError,
    refetch,
  } = useGetAssessmentByIdQuery(
    { scope: scope, id: Number(id) },
    {
      skip: !id,
    }
  );

  const [completeAssessment, { isLoading: isCompleting }] =
    useCompleteAssessmentMutation();
  const [restartAssessment, { isLoading: isRestarting }] =
    useUserRestartAssessmentMutation();
  const [adminRestartAssessment, { isLoading: isAdminRestarting }] =
    useAdminRestartAssessmentMutation();
  const [answerQuantitative] = useAnswerQuantitativeMutation();

  const isAssessmentComplete = assessment?.status === "COMPLETED";

  const activeRespondings =
    assessment?.company?.responding?.filter((r) =>
      isAssessmentComplete ? r.isAnswered : r.status === "ACTIVE"
    ) || [];

  const allRespondersCompleted = activeRespondings.every(
    (responding) => responding.isAnswered
  );

  const [isQuantitativeFilled, setIsQuantitativeFilled] = useState(false);

  useEffect(() => {
    if (assessment?.quantitativeValues) {
      setLocalQuantitativeValues(assessment.quantitativeValues);
      if (assessment.quantitativeValues.length > 0) {
        setIsQuantitativeFilled(true);
      }
    }
  }, [assessment]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleCompleteAssessment = async () => {
    try {
      await completeAssessment({ id: Number(id) }).unwrap();
      confirmModal.closeModal();
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRestartAssessment = async () => {
    try {
      if (scope === "admin") {
        await adminRestartAssessment({ id: Number(id) }).unwrap();
      } else {
        await restartAssessment({ id: Number(id) }).unwrap();
      }
      updatedConfirmModal.closeModal();
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  const handleQuantitativeSubmit = async (
    payload: QuantitativeValuesPayload
  ) => {
    try {
      setIsQuantitativeSubmitting(true);
      await answerQuantitative(payload).unwrap();
      setIsQuantitativeFilled(true);
      const result = await refetch();
      if (result.data) {
        setLocalQuantitativeValues(result.data.quantitativeValues);
      }
    } catch (err) {
      console.error("Error saving quantitative data:", err);
    } finally {
      setIsQuantitativeSubmitting(false);
    }
  };

  const renderStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      {
        color: string;
        icon: any;
        label: string;
      }
    > = {
      PENDING: {
        color:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
        icon: HiOutlineClipboardDocumentList,
        label: t("assessment.detail.status.PENDING"),
      },
      COMPLETED: {
        color:
          "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
        icon: HiOutlineClipboardDocumentList,
        label: t("assessment.detail.status.COMPLETED"),
      },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium ${config.color}`}
      >
        <Icon className="h-3.5 w-3.5" />
        {config.label}
      </span>
    );
  };

  const canComplete =
    !isAssessmentComplete &&
    allRespondersCompleted &&
    isQuantitativeFilled &&
    activeRespondings.length > 0;

  if (isFetching) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="loader w-8 text-brand-500" />
      </div>
    );
  }

  if (isError || !assessment) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <HiOutlineClipboardDocumentList className="mx-auto h-16 w-16 text-gray-400" />
          <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("assessment.detail.notFound")}
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t("assessment.detail.notFoundDesc")}
          </p>
          <Button
            variant="primary"
            className="mt-4"
            onClick={() => navigate(-1)}
          >
            {t("assessment.detail.back")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6">
      <AssessmentHeader
        assessment={assessment}
        isAssessmentComplete={isAssessmentComplete}
        allRespondersCompleted={allRespondersCompleted}
        isQuantitativeFilled={isQuantitativeFilled}
        isSubmitting={isCompleting}
        canComplete={canComplete}
        onBack={() => navigate(-1)}
        onComplete={confirmModal.openModal}
        onRefresh={handleRefresh}
        onUpdated={updatedConfirmModal.openModal}
        isRefreshing={isRefreshing}
        renderStatusBadge={renderStatusBadge}
      />

      <AssessmentStats
        assessment={assessment}
        activeRespondingsCount={activeRespondings.length}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="my-10"
      >
        <div className="relative flex gap-2 rounded-xl bg-brand-300 p-2 dark:bg-navy-700 md:gap-4 md:p-3">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
            relative flex flex-1 items-center justify-center gap-2 whitespace-nowrap 
            rounded-lg px-4 py-3 text-sm font-bold transition-all duration-300
            ${
              isActive
                ? "bg-white text-brand-600 shadow-lg ring-2 ring-brand-200 dark:bg-navy-500 dark:text-white dark:ring-navy-400"
                : "bg-white/20 text-white backdrop-blur-sm hover:bg-white/40 hover:text-white dark:bg-navy-600/50 dark:text-gray-300 dark:hover:bg-navy-600/70 dark:hover:text-white"
            }
            ${!isActive && "cursor-pointer hover:scale-[1.02] hover:shadow-md"}
          `}
              >
                <Icon
                  className={`
              inline-block h-4 w-4 transition-colors duration-300
              ${
                isActive
                  ? "text-brand-500 dark:text-white"
                  : "text-gray-200 dark:text-gray-300"
              }
            `}
                />
                <span className="transition-colors duration-300">
                  {tab.label}
                </span>
                {isActive && (
                  <motion.span
                    layoutId="activeTabIndicator"
                    className="absolute bottom-1 left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-full bg-brand-500 dark:bg-white"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {activeTab === "responders" && (
          <motion.div
            key="responders"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <RespondersSection activeRespondings={activeRespondings} />
          </motion.div>
        )}

        {activeTab === "quantitative" && (
          <motion.div
            key="quantitative"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <QuantitativeForm
              onSubmit={handleQuantitativeSubmit}
              isSubmitting={isQuantitativeSubmitting}
              assessmentId={Number(id)}
              initialValues={localQuantitativeValues}
              isCompleted={isAssessmentComplete}
              company={assessment.company}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={confirmModal.closeModal}
        handler={handleCompleteAssessment}
        loading={isCompleting}
        primary={true}
        title={t("assessment.detail.complete.title")}
        desc={t("assessment.detail.complete.desc")}
      />

      <ConfirmModal
        isOpen={updatedConfirmModal.isOpen}
        onClose={updatedConfirmModal.closeModal}
        handler={handleRestartAssessment}
        loading={isRestarting || isAdminRestarting}
        title={t("assessment.detail.updated.title")}
        desc={t("assessment.detail.updated.desc")}
        primaryText={t("common.yes")}
        secondaryText={t("common.no")}
        error
      />
    </div>
  );
}
